import React, { useEffect, useRef, useState } from 'react';
import { Animated, ScrollView, StyleSheet, TVEventHandler, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import PropTypes from 'prop-types';

import { Theme } from '@constants';

import { isWeb } from '../../../helpers/Platform';

export const TWCScrollView = ({
  children,
  style,
  isFocused,
  scrollHeight = Theme.px(500),
  scrollIncrement = Theme.px(100),
  testID,
  showLinearGradient = false,
  gradientColors = [],
}) => {
  const [completeScrollBarHeight, setCompleteScrollBarHeight] = useState(1);
  const [visibleScrollBarHeight, setVisibleScrollBarHeight] = useState(0);
  const scrollIndicator = useRef(new Animated.Value(0)).current;
  const [scrollOffset, setScrollOffset] = useState(0);
  const [contentHeight, setContentHeight] = useState(scrollHeight);
  const scrollViewReference = useRef();

  const scrollHandler = keyEvent => {
    if (isFocused) {
      const key = keyEvent.key;

      switch (key) {
        case 'ArrowUp': {
          const newOffset = Math.max(scrollOffset - scrollIncrement, 0);

          if (newOffset !== scrollOffset) {
            setScrollOffset(newOffset);
            keyEvent.preventDefault && keyEvent.preventDefault();
          }
          break;
        }
        case 'ArrowDown': {
          const newOffset = Math.min(scrollOffset + scrollIncrement, contentHeight - scrollHeight);

          if (newOffset !== scrollOffset) {
            setScrollOffset(newOffset);
            keyEvent.preventDefault && keyEvent.preventDefault();
          }
          break;
        }
      }
    }
  };

  useEffect(() => {
    const tvEventHandler = new TVEventHandler();

    if (isWeb) {
      document.addEventListener('keydown', scrollHandler, true);
    } else {
      const handleEvent = (cmp, event) => {
        if (
          isFocused &&
          event &&
          event.eventType &&
          (event.eventType === 'down' || event.eventType === 'up') &&
          event.eventKeyAction === 0
        ) {
          scrollHandler({
            key: event.eventType === 'down' ? 'ArrowDown' : 'ArrowUp',
          });
        }
      };

      tvEventHandler.enable(null, handleEvent);
    }
    scrollViewReference.current?.scrollTo({
      x: 0,
      y: Math.min(scrollOffset, contentHeight),
      animated: true,
    });
    return () => {
      if (isWeb) {
        document.removeEventListener('keydown', scrollHandler, true);
      } else {
        tvEventHandler?.disable();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollOffset, contentHeight, isFocused]);

  const scrollIndicatorSize =
    completeScrollBarHeight > visibleScrollBarHeight
      ? (visibleScrollBarHeight * visibleScrollBarHeight) / completeScrollBarHeight
      : visibleScrollBarHeight;

  const difference = visibleScrollBarHeight > scrollIndicatorSize ? visibleScrollBarHeight - scrollIndicatorSize : 1;

  const scrollIndicatorPosition = Animated.multiply(
    scrollIndicator,
    visibleScrollBarHeight / completeScrollBarHeight,
  ).interpolate({
    extrapolate: 'clamp',
    inputRange: [0, difference],
    outputRange: [0, difference],
  });

  const onContentSizeChange = (_, _contentHeight) => {
    setCompleteScrollBarHeight(_contentHeight);

    if (scrollOffset > _contentHeight - scrollHeight) {
      setScrollOffset(_contentHeight - scrollHeight);
    }
    setContentHeight(_contentHeight);
  };

  const onLayout = ({
    nativeEvent: {
      layout: { height },
    },
  }) => {
    setVisibleScrollBarHeight(height);
  };

  return (
    <View style={styles.scrollContainer}>
      <ScrollView
        accessible={false}
        nativeID={testID}
        ref={scrollViewReference}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        style={[styles.scrollViewContainer, style]}
        testID={testID}
        onContentSizeChange={onContentSizeChange}
        onLayout={onLayout}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollIndicator } } }], {
          useNativeDriver: false,
        })}
      >
        <>
          {children}
          <View style={styles.footer} />
        </>
      </ScrollView>
      {showLinearGradient && (
        <LinearGradient colors={gradientColors} pointerEvents={'none'} style={styles.alertModalGradient} />
      )}
      <View style={styles.customScrollBarBackground}>
        <Animated.View
          style={[
            styles.customScrollBar,
            {
              height: scrollIndicatorSize,
              transform: [{ translateY: scrollIndicatorPosition }],
            },
          ]}
        />
      </View>
    </View>
  );
};

TWCScrollView.propTypes = {
  children: PropTypes.node.isRequired,
  isFocused: PropTypes.bool,
  scrollHeight: PropTypes.number,
  scrollIncrement: PropTypes.number,
  testID: PropTypes.string,
  showLinearGradient: PropTypes.bool,
  gradientColors: PropTypes.array,
};

TWCScrollView.defaultProps = {
  isFocused: false,
  scrollHeight: Theme.px(500),
  scrollIncrement: Theme.px(100),
  testID: null,
  showLinearGradient: false,
  gradientColors: [],
};

const styles = StyleSheet.create({
  alertModalGradient: {
    bottom: 0,
    height: Theme.px(180),
    left: 0,
    position: 'absolute',
    width: '100%',
  },
  customScrollBar: {
    backgroundColor: Theme.whisperWhite,
    borderRadius: Theme.px(4),
    width: Theme.px(8),
  },
  customScrollBarBackground: {
    backgroundColor: Theme.withOpacity(Theme.suvaGrey, 0.2),
    borderRadius: Theme.px(4),
    height: '100%',
    width: Theme.px(8),
  },
  footer: {
    height: Theme.px(100),
  },
  scrollContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  scrollViewContainer: {
    width: '100%',
  },
});
